/**
 * studentLiveMachine — le live tel que l'élève le vit, posé sur la salle (`live/room`) : le mode que
 * pose le tuteur, la main levée, ce que montre un téléphone, l'activité que le tuteur pousse, la fin.
 *
 * Pure : des événements en entrée (le canal de la salle, les attributs du participant, les réponses
 * du serveur, les gestes de l'élève), l'état et les signaux de la transition en sortie. Le crochet
 * (`useStudentLive`) joue les signaux : un son, la conversation de Sam à fermer, l'écran de fin.
 *
 * Les règles d'aujourd'hui, et ce qui change :
 *  - **le son** suit `serverTruth` : la valeur persistée d'une séance d'avant ne compte plus, un
 *    attribut vide de reconnexion n'est plus lu comme un Concentré, une réponse plus ancienne que le
 *    dernier geste du tuteur ne l'écrase plus. Un geste du tuteur (un attribut) se dit : son son, et
 *    en Concentré la bulle de Sam (`LKEvents` disait « X t'a mis en mode concentré »). Après une
 *    reprise, c'est la machine qui repose le mode sur la salle (`restore_sound`), depuis la relecture
 *    du serveur, et non plus `LKEvents` depuis Redux ;
 *  - **la vue** : à l'entrée, la scène, puis l'élève bascule d'un geste. Au grand écran, elle revient à la
 *    scène à chaque changement de mode : l'élève voit son masteur derrière Sam à chaque arrivée en
 *    Concentré (Wajih, 22/10 : Sam seul, il ne comprenait pas qu'un masteur était là). Au téléphone, un
 *    changement de mode ne déplace plus l'élève de l'onglet où il est (Wajih, 24/10 : renvoyé sur la visio
 *    au retour en Dialogue, il quittait son exercice) ; sur l'activité, le retour en Dialogue se dit
 *    (`tutorTalking`) ;
 *  - **le micro touché en Concentré** dit pourquoi il ne s'ouvre pas (`micHint`) ;
 *  - **l'activité poussée** (`newTodo` avec `forceFocus`) remplace la courante, avec son son, son
 *    bandeau, la vue sur l'activité, et la conversation de Sam en cours fermée ;
 *  - **l'activité ajoutée** (`newTodo` sans `forceFocus`, une activité que l'élève n'avait pas : le tuteur
 *    l'a créée sans « Afficher à l'élève ») se dit, sans rien ouvrir : « Nouvelle activité de ton masteur ·
 *    Voir » (`addedActivityId`), jusqu'à ce que l'élève l'ait devant lui (ouverte, et à l'écran, ce que dit
 *    la page) ; une activité qu'il choisit lui-même mène, au téléphone, sur l'onglet Activité ;
 *  - **Sam envoyé par le tuteur** (`checkpointTriggerResponse` accepté, la main levée de l'élève ;
 *    Wajih, 20/10) : Sam passe devant quel que soit le mode, la phrase le dit, jusqu'à ce que sa
 *    conversation se referme. Une conversation déjà ouverte est gardée : on n'en empile pas une seconde ;
 *  - **la fin** arrive par le canal (`finalizeLive`, l'accélérateur) ou par le relevé
 *    (`today_occurrence.finalized`, qui fait foi) : un relevé qui dit « close » ferme la séance même
 *    si `finalizeLive` s'est perdu sur une salle fermée, et un relevé DEMANDÉ après la fin qui dit
 *    « pas close » la rouvre (le BO l'a rouverte) ;
 *  - **la fin croise la sortie de l'élève** : arrivée pendant qu'il sort, elle mène tout de suite au
 *    rapport, en élève déjà sorti ; arrivée une fois sa sortie partie, à rien. Le rapport survit à la
 *    sortie du live (`live-end`) : rien d'autre ne l'en sort que son geste.
 */
import { applyGesture, applyPush, applyReply, isStaleReply, unknownTruth, type Truth } from "./serverTruth"

export type StudentLiveView = "stage" | "activity"

/** Un son que l'écran joue. */
export type StudentLiveCue = "focused" | "dialogue" | "new_activity"

export type StudentLiveState = {
    /** `true` : Dialogue (le tuteur parle avec l'élève) ; `false` : Concentré. */
    dialogue: Truth
    /**
     * La main levée, telle que l'élève la voit : pendant l'envoi, la valeur de son geste (le bouton
     * change au toucher, sans attendre le serveur) ; ensuite, ce que le serveur dit.
     */
    handRaised: Truth
    /** La main levée part : un second toucher attend la réponse. */
    handPending: boolean
    /** Pendant l'envoi : la main d'avant le geste, que reprend un échec. `null` hors envoi. */
    handBefore: Truth | null
    view: StudentLiveView
    /**
     * Une vue à la fois, Activité ou Visio : le téléphone, que la page dit (`LAYOUT_CHANGED`). Jusque-là,
     * la règle du grand écran : la vue revient à la scène à chaque changement de mode.
     */
    tabs: boolean
    micHint: boolean
    /** Sam vient de parler (le passage en Concentré). */
    samBubble: boolean
    /**
     * « Ton masteur te parle · Voir la visio » : au téléphone, sur l'activité, le tuteur vient de repasser
     * en Dialogue. Retiré dès que l'élève est sur la visio, au repassage en Concentré, quand le tuteur envoie
     * Sam, ou par la page, son délai passé (`TUTOR_TALKING_DISMISSED`).
     */
    tutorTalking: boolean
    /** « Nouvelle activité affichée ». */
    newActivity: boolean
    /**
     * « Nouvelle activité de ton masteur · Voir » : l'activité que le tuteur vient d'ajouter sans l'afficher.
     * Retirée dès que l'élève l'a devant lui (ouverte, et l'activité à l'écran), quand le tuteur en affiche
     * une, ou par l'écran (son délai passé, l'activité retirée : `ADDED_ACTIVITY_DISMISSED`).
     */
    addedActivityId: number | null
    /** L'activité se voit : la page le dit (`ACTIVITY_ON_SCREEN`), elle seule sait sa mise en page. */
    activityOnScreen: boolean
    /**
     * Le tuteur a envoyé Sam à l'élève : Sam passe devant, jusqu'à ce que sa conversation se referme (ou
     * que son ouverture échoue).
     */
    samSent: boolean
    /** « Ton masteur n'est pas disponible, Sam s'occupe de toi en attendant. » */
    samSentNotice: boolean
    /** L'activité ouverte, choisie ou poussée. `null` : rien d'ouvert (l'écran choisit dans la liste). */
    activityId: number | null
    /**
     * La fin, et ce qui la date : l'heure du `finalizeLive`, ou celle où le relevé qui l'a lue a été
     * demandé. Seul un relevé demandé APRÈS peut la défaire.
     */
    ended: { via: "channel" | "snapshot"; since: number } | null
    /**
     * La sortie de l'élève : engagée (« Terminer », « Pas maintenant », « Quitter »), puis partie (le
     * live se quitte). La fin qui la croise mène au rapport en élève déjà sorti, ou à rien une fois
     * la sortie partie.
     */
    exit: "none" | "committed" | "out"
}

export type StudentLiveEvent =
    | { type: "SOUND_PUSH"; raw: string | null | undefined; at: number }
    | { type: "HAND_PUSH"; raw: string | null | undefined; at: number }
    /**
     * La réponse de `joinCall`, `getCall` ou `raiseHand` : ce que le serveur sait, demandé à `requestedAt`.
     * `resync` : la relecture d'après une reprise, qui repose ensuite le mode sur la salle.
     */
    | {
          type: "CALL_REPLY"
          dialogue: boolean | null
          handRaised: boolean | null
          requestedAt: number
          resync?: boolean
      }
    /** La relecture d'après une reprise a échoué : le mode se repose quand même, avec ce que l'élève sait. */
    | { type: "RESYNC_UNREAD" }
    /** Le toucher de la main, à `at` : une réponse demandée avant ne la défait pas. */
    | { type: "HAND_TOGGLED"; at: number }
    | { type: "HAND_FAILED" }
    | { type: "VIEW_CHANGED"; view: StudentLiveView }
    /** La page dit sa forme : `tabs`, une vue à la fois (le téléphone). */
    | { type: "LAYOUT_CHANGED"; tabs: boolean }
    | { type: "TUTOR_TALKING_DISMISSED" }
    | { type: "MIC_TOUCHED" }
    | { type: "MIC_HINT_DISMISSED" }
    | { type: "SAM_BUBBLE_DISMISSED" }
    /** `known` : l'élève avait déjà l'activité (un titre modifié, une activité rendue à la liste qu'il avait). */
    | { type: "NEW_TODO"; activityId: number | null; forceFocus: boolean; known?: boolean }
    | { type: "NEW_ACTIVITY_DISMISSED" }
    | { type: "ADDED_ACTIVITY_DISMISSED" }
    /** `chosen` : le geste de l'élève (« Mes activités », la barre de l'activité ajoutée) — au téléphone, la vue la suit. */
    | { type: "ACTIVITY_SELECTED"; activityId: number | null; chosen?: boolean }
    | { type: "ACTIVITY_ON_SCREEN"; onScreen: boolean }
    /**
     * Le tuteur envoie Sam (`checkpointTriggerResponse` accepté). `kept` : une conversation de Sam était
     * déjà ouverte, elle reste, aucune autre ne s'ouvre.
     */
    | { type: "SAM_SENT"; kept: boolean }
    /** La conversation de Sam s'est refermée, ou son ouverture a échoué : Sam rend la main au mode. */
    | { type: "SAM_RELEASED" }
    | { type: "SAM_SENT_NOTICE_DISMISSED" }
    | { type: "CHANNEL_FINALIZE"; at: number }
    | { type: "SNAPSHOT"; finalized: boolean; requestedAt: number }
    | { type: "LEAVE_COMMITTED" }
    | { type: "LEAVE_OUT" }

/**
 * Le rapport de la fin : plus tard (le délai d'aujourd'hui), tout de suite en élève déjà sorti (`left` :
 * sa note donnée ou déclinée), ou pas du tout (sa sortie est partie).
 */
export type StudentLiveReport = "later" | "left" | "none"

export type StudentLiveSignal =
    | { type: "transition"; from: string; to: string; event: string }
    | { type: "cue"; cue: StudentLiveCue }
    /** Le tuteur a poussé une activité : la conversation de Sam en cours se ferme. */
    | { type: "close_checkpoint" }
    | { type: "ended"; via: "channel" | "snapshot"; report: StudentLiveReport }
    /** La sortie engagée sur une séance déjà close : le rapport, en élève déjà sorti. */
    | { type: "report_left" }
    | { type: "reopened" }
    /**
     * Repose le mode sur l'attribut `sound_mode` du participant : une reconnexion complète rejoint la
     * salle avec des attributs vides, et le tuteur y lirait un Concentré.
     */
    | { type: "restore_sound"; dialogue: boolean }

export const initialStudentLiveState: StudentLiveState = {
    dialogue: unknownTruth(false),
    handRaised: unknownTruth(false),
    handPending: false,
    handBefore: null,
    view: "stage",
    tabs: false,
    micHint: false,
    samBubble: false,
    tutorTalking: false,
    newActivity: false,
    addedActivityId: null,
    activityOnScreen: false,
    samSent: false,
    samSentNotice: false,
    activityId: null,
    ended: null,
    exit: "none",
}

const REPORT_FOR_EXIT: Record<StudentLiveState["exit"], StudentLiveReport> = {
    none: "later",
    committed: "left",
    out: "none",
}

type Step = { state: StudentLiveState; signals: StudentLiveSignal[] }

const NOTHING = (state: StudentLiveState): Step => ({ state, signals: [] })
const modeName = (dialogue: boolean) => (dialogue ? "dialogue" : "focused")

/**
 * Le mode a changé. Au grand écran, la vue revient à la scène (le masteur, devant en Dialogue, derrière
 * Sam en Concentré) ; au téléphone, l'élève reste sur son onglet. Le geste du tuteur se dit : Sam parle au
 * passage en Concentré, et le retour en Dialogue sur l'activité d'un téléphone a sa barre.
 */
function afterMode(previous: StudentLiveState, next: StudentLiveState, event: string, pushed: boolean): Step {
    const was = previous.dialogue.value
    const now = next.dialogue.value
    if (now === was) return NOTHING(next)
    const signals: StudentLiveSignal[] = [{ type: "transition", from: modeName(was), to: modeName(now), event }]
    if (pushed) signals.push({ type: "cue", cue: now ? "dialogue" : "focused" })
    const view = next.tabs ? next.view : "stage"
    return {
        state: {
            ...next,
            view,
            micHint: now ? false : next.micHint,
            samBubble: pushed && !now,
            tutorTalking: pushed && now && view === "activity",
        },
        signals,
    }
}

/** L'activité ajoutée est devant l'élève (ouverte, et à l'écran) : sa barre n'a plus rien à dire. */
function withAddedSeen(state: StudentLiveState): StudentLiveState {
    const seen = state.addedActivityId != null && state.addedActivityId === state.activityId && state.activityOnScreen
    return seen ? { ...state, addedActivityId: null } : state
}

/**
 * Après une reprise, le mode se repose sur la salle depuis cette machine, seule source quand l'écran
 * est monté : la valeur que le serveur vient de dire, ou le geste du tuteur arrivé depuis.
 */
function withSoundRestored(step: Step): Step {
    return { ...step, signals: [...step.signals, { type: "restore_sound", dialogue: step.state.dialogue.value }] }
}

export function studentLiveReducer(state: StudentLiveState, event: StudentLiveEvent): Step {
    switch (event.type) {
        case "SOUND_PUSH": {
            const dialogue = applyPush(state.dialogue, event.raw, event.at)
            if (dialogue === state.dialogue) return NOTHING(state)
            return afterMode(state, { ...state, dialogue }, "attribute", true)
        }

        case "HAND_PUSH": {
            const handRaised = applyPush(state.handRaised, event.raw, event.at)
            if (handRaised === state.handRaised) return NOTHING(state)
            // L'attribut est le serveur : un échec de l'envoi ne revient plus en arrière.
            return NOTHING({ ...state, handRaised, handPending: false, handBefore: null })
        }

        case "CALL_REPLY": {
            let next = state
            // Une réponse demandée avant le geste (une relecture de `getCall` partie avant le toucher)
            // ne lève ni l'attente ni la main : seule une réponse plus récente la confirme.
            if (event.handRaised != null && !isStaleReply(next.handRaised, event.requestedAt)) {
                next = {
                    ...next,
                    handRaised: applyReply(next.handRaised, event.handRaised, event.requestedAt),
                    handPending: false,
                    handBefore: null,
                }
            }
            const step =
                event.dialogue == null
                    ? NOTHING(next)
                    : afterMode(
                          state,
                          { ...next, dialogue: applyReply(next.dialogue, event.dialogue, event.requestedAt) },
                          "reply",
                          false
                      )
            return event.resync === true ? withSoundRestored(step) : step
        }

        case "RESYNC_UNREAD":
            return withSoundRestored(NOTHING(state))

        case "HAND_TOGGLED":
            // Le bouton suit le geste tout de suite ; la réponse, ou l'attribut, le confirme.
            if (state.handPending) return NOTHING(state)
            return NOTHING({
                ...state,
                handRaised: applyGesture(state.handRaised, !state.handRaised.value, event.at),
                handPending: true,
                handBefore: state.handRaised,
            })

        case "HAND_FAILED":
            // L'envoi n'est pas passé : la main reprend sa place d'avant le geste.
            if (!state.handPending) return NOTHING(state)
            return NOTHING({
                ...state,
                handRaised: state.handBefore ?? state.handRaised,
                handPending: false,
                handBefore: null,
            })

        case "VIEW_CHANGED":
            if (event.view === state.view) return NOTHING(state)
            // Sur la visio, l'élève voit son masteur lui parler : la barre n'a plus rien à dire.
            return NOTHING({ ...state, view: event.view, tutorTalking: state.tutorTalking && event.view !== "stage" })

        case "LAYOUT_CHANGED":
            if (event.tabs === state.tabs) return NOTHING(state)
            return NOTHING({ ...state, tabs: event.tabs, tutorTalking: state.tutorTalking && event.tabs })

        case "TUTOR_TALKING_DISMISSED":
            return state.tutorTalking ? NOTHING({ ...state, tutorTalking: false }) : NOTHING(state)

        case "MIC_TOUCHED":
            // En Dialogue, le micro est à l'élève : le toucher le coupe ou l'ouvre, rien à dire.
            return state.dialogue.value ? NOTHING(state) : NOTHING({ ...state, micHint: true })

        case "MIC_HINT_DISMISSED":
            return state.micHint ? NOTHING({ ...state, micHint: false }) : NOTHING(state)

        case "SAM_BUBBLE_DISMISSED":
            return state.samBubble ? NOTHING({ ...state, samBubble: false }) : NOTHING(state)

        case "NEW_TODO":
            if (event.activityId == null) return NOTHING(state)
            // Sans `forceFocus`, la liste se relit et rien ne s'ouvre ; une activité que l'élève n'avait pas se
            // dit (le tuteur l'a créée sans l'afficher). Une activité qu'il avait (un titre modifié) : rien.
            if (!event.forceFocus) {
                if (event.known || event.activityId === state.activityId) return NOTHING(state)
                return {
                    state: { ...state, addedActivityId: event.activityId },
                    signals: [{ type: "transition", from: "activity", to: "added_activity", event: "newTodo" }],
                }
            }
            return {
                state: {
                    ...state,
                    activityId: event.activityId,
                    newActivity: true,
                    view: "activity",
                    // Le tuteur affiche une activité : la sienne passe devant celle qu'il avait ajoutée.
                    addedActivityId: null,
                },
                signals: [
                    { type: "transition", from: "activity", to: "pushed_activity", event: "newTodo" },
                    { type: "cue", cue: "new_activity" },
                    { type: "close_checkpoint" },
                ],
            }

        case "SAM_SENT":
            return {
                // Sam passe devant : le masteur n'est plus là pour l'élève.
                state: { ...state, samSent: true, samSentNotice: true, tutorTalking: false },
                signals: [
                    {
                        type: "transition",
                        from: state.samSent ? "sam_sent" : "in_live",
                        to: "sam_sent",
                        event: event.kept ? "sam_sent_kept_open" : "sam_sent",
                    },
                ],
            }

        case "SAM_RELEASED":
            if (!state.samSent) return NOTHING(state)
            return {
                state: { ...state, samSent: false },
                signals: [{ type: "transition", from: "sam_sent", to: "in_live", event: "sam_released" }],
            }

        case "SAM_SENT_NOTICE_DISMISSED":
            return state.samSentNotice ? NOTHING({ ...state, samSentNotice: false }) : NOTHING(state)

        case "NEW_ACTIVITY_DISMISSED":
            return state.newActivity ? NOTHING({ ...state, newActivity: false }) : NOTHING(state)

        case "ADDED_ACTIVITY_DISMISSED":
            return state.addedActivityId != null ? NOTHING({ ...state, addedActivityId: null }) : NOTHING(state)

        case "ACTIVITY_SELECTED": {
            const view = event.chosen && state.tabs ? "activity" : state.view
            if (event.activityId === state.activityId && view === state.view) return NOTHING(state)
            return NOTHING(withAddedSeen({ ...state, activityId: event.activityId, view }))
        }

        case "ACTIVITY_ON_SCREEN":
            if (event.onScreen === state.activityOnScreen) return NOTHING(state)
            return NOTHING(withAddedSeen({ ...state, activityOnScreen: event.onScreen }))

        case "CHANNEL_FINALIZE":
            if (state.ended != null) return NOTHING(state)
            return {
                state: { ...state, ended: { via: "channel", since: event.at } },
                signals: [
                    { type: "transition", from: exitPhase(state), to: "ended", event: "finalizeLive" },
                    { type: "ended", via: "channel", report: REPORT_FOR_EXIT[state.exit] },
                ],
            }

        case "SNAPSHOT": {
            if (event.finalized) {
                if (state.ended != null) return NOTHING(state)
                return {
                    state: { ...state, ended: { via: "snapshot", since: event.requestedAt } },
                    signals: [
                        { type: "transition", from: exitPhase(state), to: "ended", event: "snapshot" },
                        { type: "ended", via: "snapshot", report: REPORT_FOR_EXIT[state.exit] },
                    ],
                }
            }
            // Un relevé demandé avant la fin ne dit rien d'elle : il décrit la séance d'avant.
            if (state.ended == null || event.requestedAt <= state.ended.since) return NOTHING(state)
            return {
                state: { ...state, ended: null },
                signals: [
                    { type: "transition", from: "ended", to: "in_live", event: "reopened" },
                    { type: "reopened" },
                ],
            }
        }

        case "LEAVE_COMMITTED":
            if (state.exit !== "none") return NOTHING(state)
            return {
                state: { ...state, exit: "committed" },
                signals: [
                    { type: "transition", from: "in_live", to: "leaving", event: "leave" },
                    ...(state.ended != null ? [{ type: "report_left" } as const] : []),
                ],
            }

        case "LEAVE_OUT":
            return state.exit === "out" ? NOTHING(state) : NOTHING({ ...state, exit: "out" })
    }
}

/** Le délai laissé à la page pour montrer une activité affichée : largement plus que la bascule. */
export const PUSHED_ON_SCREEN_WITHIN_MS = 8 * 1000

/**
 * Le masteur a affiché cette activité, l'élève n'a pas quitté sa vue, et la page ne la dit toujours pas à
 * l'écran. Un élève reparti sur la visio entre-temps, ou sur une autre activité, n'est pas une panne.
 */
export function pushedActivityOutOfSight(state: StudentLiveState, activityId: number): boolean {
    return state.activityId === activityId && state.view === "activity" && !state.activityOnScreen
}

function exitPhase(state: StudentLiveState): string {
    return state.exit === "none" ? "in_live" : state.exit === "committed" ? "leaving" : "left"
}

/** La réponse d'un appel (`joinCall`, `getCall`, `raiseHand`) → ce qu'elle dit de l'élève. */
export function callReplyFor(
    data: { sound_mode_ids?: unknown; raised_hands_ids?: unknown } | null | undefined,
    userId: number | string | null | undefined
): { dialogue: boolean | null; handRaised: boolean | null } {
    const includes = (ids: unknown): boolean | null =>
        Array.isArray(ids) ? ids.map(String).includes(String(userId)) : null
    return { dialogue: includes(data?.sound_mode_ids), handRaised: includes(data?.raised_hands_ids) }
}
